import { createClient as createSupabaseClient, SupabaseClient } from '@supabase/supabase-js'

// Lazy-load environment variables to avoid initialization issues
const getEnvVars = () => {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
  const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

  // Validate environment variables
  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Missing Supabase environment variables. Please check NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.')
  }
  
  return { supabaseUrl, supabaseKey }
}

// Singleton pattern for Supabase client
class SupabaseClientManager {
  private static instance: SupabaseClient | null = null
  private static adminInstance: SupabaseClient | null = null

  public static getInstance(): SupabaseClient {
    if (!SupabaseClientManager.instance) {
      const { supabaseUrl, supabaseKey } = getEnvVars()
      SupabaseClientManager.instance = createSupabaseClient(supabaseUrl, supabaseKey)
    }
    return SupabaseClientManager.instance
  }

  public static getAdminInstance(): SupabaseClient {
    // Only allow admin client on server side
    if (typeof window !== 'undefined') {
      throw new Error('Admin client cannot be used on client side')
    }
    
    if (!SupabaseClientManager.adminInstance) {
      const { supabaseUrl } = getEnvVars()
      const adminKey = process.env.SUPABASE_SERVICE_ROLE_KEY!
      if (!adminKey) {
        throw new Error('Missing SUPABASE_SERVICE_ROLE_KEY for admin client')
      }
      SupabaseClientManager.adminInstance = createSupabaseClient(supabaseUrl, adminKey)
    }
    return SupabaseClientManager.adminInstance
  }

  public static createCustomClient(customUrl: string, customKey: string): SupabaseClient {
    return createSupabaseClient(customUrl, customKey)
  }
}

// Export the singleton instance
export const supabase = SupabaseClientManager.getInstance()

// Export createClient function for compatibility - but always return the same instance
export function createClient(
  customUrl?: string,
  customKey?: string
) {
  // If custom parameters are provided, create a new client
  if (customUrl && customKey) {
    return SupabaseClientManager.createCustomClient(customUrl, customKey)
  }
  
  // Otherwise return the singleton instance
  return supabase
}

// Database Types - Generated from actual schema

export type Quote = {
  id: string
  created_at: string
  updated_at: string
  quote_number?: string
  quote_reference?: string
  submission_id?: string
  execution_id?: string
  status: 'pending_approval' | 'approved' | 'sent_to_client' | 'accepted' | 'rejected' | 'error' | 'post_approval' | 'client_onboarding' | 'project_setup' | 'completed' | 'skipped_client_send' | 'edited' | 'lost'
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone?: string
  company: string
  employees: number
  plan_name: string
  total_price: string
  total_price_numeric?: number
  addons?: string
  onboarding_fee_below_20?: number
  onboarding_fee_above_20?: number
  onboarding_fee_numeric?: number
  onboarding_fee_display?: string
  xero_contact_id?: string
  xero_quote_id?: string
  xero_total?: number
  xero_expiry_date?: string
  google_drive_folder_id?: string
  google_drive_folder_name?: string
  google_doc_id?: string
  google_doc_url?: string
  proposal_template_id?: string
  crm_contact_id?: string
  crm_deal_id?: string
  crm_deal_stage?: string
  resume_url?: string
  pending_approval_since?: string
  approved_by?: string
  approved_at?: string
  rejection_reason?: string
  approval_notes?: string
  xero_line_item_summary?: string
  xero_subtotal?: number
  xero_tax_amount?: number
  sent_to_client_at?: string
  client_email_sent_by?: string
  google_drive_pdf_id?: string
  google_drive_pdf_url?: string
  pdf_filename?: string
  pdf_uploaded_at?: string
  quote_type: 'subscription' | 'consulting'
  consulting_fee_numeric?: number
  consulting_fee_display?: string
  accepted_at?: string
  accepted_by?: string
  company_id?: string
  contact_id?: string
  opportunity_stage: 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost'
  probability?: number
  expected_close_date?: string
  competitor_info?: string
  decision_maker_contact_id?: string
  skip_client_send?: boolean
  skip_client_send_reason?: string
}

export type Company = {
  id: string
  created_at: string
  updated_at: string
  company_name: string
  industry?: string
  website?: string
  employee_count?: number
  annual_revenue?: number
  address_line_1?: string
  address_line_2?: string
  city?: string
  state?: string
  postal_code?: string
  country: string
  tags: string[]
  priority_level: 'low' | 'medium' | 'high'
  status: 'active' | 'inactive'
  lifecycle_stage: 'prospect' | 'lead' | 'client' | 'onboarding' | 'inactive'
  converted_at?: string
  client_value?: number
  monthly_recurring_revenue?: number
  xero_contact_id?: string
  notes?: string
  created_by?: string
  updated_by?: string
  search_vector?: string
  metadata?: any
  account_number?: string
  cro_number?: string
  contract_total?: number
  purpletree_insurance?: string
  initial_invoice_value?: number
  google_drive_folder_id?: string
  google_drive_folder_name?: string
}

export type Contact = {
  id: string
  created_at: string
  updated_at: string
  first_name: string
  last_name: string
  email?: string
  phone?: string
  status: 'lead' | 'prospect' | 'active_client' | 'inactive' | 'archived'
  company_id?: string
  is_primary_contact?: boolean
  is_hr_contact?: boolean
  is_accounts_contact?: boolean
  is_contracts_contact?: boolean
  has_hr_authorization?: boolean
  job_title?: string
  department?: string
  linkedin_url?: string
  mobile_phone?: string
  search_vector?: string
}

export type Invoice = {
  id: string
  created_at: string
  updated_at: string
  contact_id?: string
  quote_id?: string
  company_id?: string
  invoice_number?: string
  subtotal: number
  tax_amount: number
  total_amount: number
  amount_paid: number
  currency: string
  status: 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled'
  due_date?: string
  paid_date?: string
  xero_invoice_id?: string
  notes?: string
}

export type Contract = {
  id: string
  created_at: string
  updated_at: string
  company_id: string
  quote_id?: string
  title: string
  contract_type: 'hr_services' | 'consulting' | 'recurring' | 'one_time'
  contract_value: number
  currency: string
  start_date: string
  end_date?: string
  payment_terms: string
  billing_frequency: 'monthly' | 'quarterly' | 'annually' | 'one_time'
  auto_renewal: boolean
  renewal_notice_days: number
  status: 'draft' | 'sent' | 'completed' | 'active' | 'expired' | 'voided' | 'cancelled'
  envelope_id?: string
  drive_pdf_id?: string
  sent_at?: string
  signed_at?: string
  signed_by?: string
  metadata?: any
}

export type PendingApproval = {
  id: string
  created_at: string
  updated_at: string
  execution_id: string
  resume_webhook_url: string
  type: string
  title: string
  description?: string
  status: 'pending' | 'approved' | 'rejected' | 'expired'
  entity_type?: string
  entity_id?: string
  data: any
  approved_by?: string
  approved_at?: string
  rejection_reason?: string
  expires_at: string
}

export type AppConfig = {
  id: string
  created_at: string
  updated_at: string
  environment: 'sandbox' | 'live'
  xero_client_id?: string
  xero_client_secret?: string
  xero_tenant_id?: string
  gocardless_access_token?: string
  google_client_id?: string
  google_client_secret?: string
  google_api_key?: string
  n8n_webhook_url?: string
  slack_webhook_url?: string
  email_from_address?: string
  email_from_name?: string
  brand_primary_color?: string
  brand_secondary_color?: string
  brand_logo_url?: string
  onboarding_enabled: boolean
  auto_approval_threshold?: number
  quote_expiry_days?: number
  invoice_payment_terms?: string
  currency_default: string
  timezone_default: string
  notification_settings?: any
  integration_settings?: any
}

export type WebhookAction = {
  id: string
  created_at: string
  updated_at: string
  action_key: string
  action_name: string
  webhook_url: string
  enabled: boolean
  description?: string
  expected_payload?: any
  last_called_at?: string
  total_calls?: number
  success_rate?: number
  metadata?: any
}
// Default export for convenience
// Export the SupabaseClientManager class for use in other modules
export { SupabaseClientManager }

export default supabase
